import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Store } from '@ngrx/store'
import { combineLatest, Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { AppRoutesNS } from 'src/app/app-routes.namespace'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import * as fromDevice from 'src/app/features/device/device.selectors'
import { configurationOptionSelectors } from 'src/app/features/configuration-option/configuration-option.selectors'
import { Member } from 'src/app/features/member/member'
import { MenuItem } from 'src/app/menu/menu.model'
import { NavItem } from 'src/app/shared/interfaces'
import { ConfigurationService } from 'src/app/shared/services/configuration.service'
import { environment } from 'src/environments/environment'
import { MobileNavbarItem } from 'src/environments/environment-model'
import {
  ConfigurationOption,
  ConfigurationOptionKeys,
  HomePageConfigWebOption
} from 'src/app/features/configuration-option/configuration-option'
import {
  DiscoveryAreaConfig,
  DiscoveryAreaConfigType
} from 'src/app/shared/components/discovery-area/discovery-area.types'
import { ISO639LanguageCodes } from 'src/app/shared/utils/languageCodes'
import { ConfigOptionsKeys } from 'src/app/shared/constants'

interface MobileNavbarState {
  menuItems: MenuItem[]
  showScan: boolean
}

@Component({
  selector: 'primary-navbar-mobile-main-menu-block',
  templateUrl: './show.component.html'
})
export class PrimaryNavBarMobileMainMenuComponent implements OnInit {
  @Input() member$!: Observable<Member | null>

  @Output() onLinkClick = new EventEmitter<{
    item: NavItem
    event: MouseEvent
  }>()
  @Output() onClickScan = new EventEmitter<void>()
  @Output() ticketingReset = new EventEmitter<void>()

  navbarState$: Observable<MobileNavbarState> = of({
    menuItems: [],
    showScan: false
  })

  constructor(
    private store: Store<AppState>,
    private configurationService: ConfigurationService
  ) {}

  ngOnInit() {
    this.navbarState$ = combineLatest([
      this.member$,
      this.configurationService.isMobileApp$,
      this.configurationService.isRouteAllowedOnMobileApp$(
        AppRoutesNS.Scan.REDIRECT
      ),
      this.store.select(fromDevice.selectLockIsValid),
      this.store.select(configurationOptionSelectors.selectAll),
      this.store.select(settingSelectors.selectById, 'language')
    ]).pipe(
      map(
        ([
          member,
          isMobileApp,
          isScanRouteAllowed,
          lockIsValid,
          configurationOptions,
          selectedLanguage
        ]) => {
          const discoveryAreaConfig =
            this.getDiscoveryAreaConfig(configurationOptions)
          const language =
            member?.language ?? selectedLanguage ?? ISO639LanguageCodes.English
          const showScan =
            Boolean(member) && isMobileApp && isScanRouteAllowed && lockIsValid
          const menuItems = environment.mobileNavbarItems.map((item) => ({
            translateString: this.resolveMobileNavbarTranslation(
              item,
              configurationOptions,
              discoveryAreaConfig,
              language
            ),
            link: item.url,
            queryParams: item.queryParams,
            icon: item.icon,
            restrictToRoles: []
          }))

          return { menuItems, showScan }
        }
      )
    )
  }

  handleLinkClick(item: NavItem, event: MouseEvent) {
    this.onLinkClick.emit({ item, event })
  }

  getScanButtonIndex(menuItems: MenuItem[]): number {
    return Math.ceil(menuItems.length / 2)
  }

  private getDiscoveryAreaConfig(
    configurationOptions: ConfigurationOption[]
  ): DiscoveryAreaConfig[] {
    const discoveryAreaConfigOption = configurationOptions.find(
      (c) => c.key == ConfigurationOptionKeys.DISCOVERY_AREA_CONFIG
    )

    if (!discoveryAreaConfigOption?.value) {
      return null
    }

    return JSON.parse(discoveryAreaConfigOption.value)
  }

  private getDiscoveryTitle(
    discoveryAreaConfig: DiscoveryAreaConfig[],
    type: DiscoveryAreaConfigType,
    selectedLanguage: ISO639LanguageCodes
  ): string {
    return discoveryAreaConfig?.find((x) => x.type == type)?.[
      'title_' + selectedLanguage
    ]
  }

  private resolveMobileNavbarTranslation(
    item: MobileNavbarItem,
    configurationOptions: ConfigurationOption[],
    discoveryAreaConfig: DiscoveryAreaConfig[],
    selectedLanguage: ISO639LanguageCodes
  ): string {
    switch (item.url) {
      case AppRoutesNS.Home.HOME:
        return this.getHomePageTitle(configurationOptions, selectedLanguage)
      case AppRoutesNS.Booking.ACTIVITIES:
        return this.getDiscoveryTitle(
          discoveryAreaConfig,
          DiscoveryAreaConfigType.ACTIVITY_DISCOVERY,
          selectedLanguage
        )
      case AppRoutesNS.Booking.RENT:
        return this.getDiscoveryTitle(
          discoveryAreaConfig,
          DiscoveryAreaConfigType.RENTAL_DISCOVERY,
          selectedLanguage
        )
      case AppRoutesNS.Overview.OVERVIEW:
        return this.getDiscoveryTitle(
          discoveryAreaConfig,
          DiscoveryAreaConfigType.OVERVIEW_DISCOVERY,
          selectedLanguage
        )
      default:
        return item.key
    }
  }

  private getHomePageTitle(
    configurationOptions: ConfigurationOption[],
    selectedLanguage: ISO639LanguageCodes
  ): string {
    const homePageConfigOption = configurationOptions.find(
      (c) => c.key == ConfigOptionsKeys.HOME_PAGE_CONFIG
    )
    const homePageConfig: HomePageConfigWebOption | null =
      homePageConfigOption?.value
        ? JSON.parse(homePageConfigOption.value)
        : null

    return (
      homePageConfig?.title?.[
        selectedLanguage as keyof HomePageConfigWebOption['title']
      ] ?? 'general.home'
    )
  }
}
